import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faUserPlus, faUserSlash } from "@fortawesome/pro-regular-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import { extractDomain } from "@/utils/extractDomain";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";

import {
	isRowTakenError,
	useSelectProspectForListPersonMutation,
} from "@/pages/Private/redux/list/list-person-prospect.api";

interface FeedSelectProspectProps {
	card: FeedRunCard;
	/** The card's placeholder lead (needsProspectSelection) the picked prospect attaches to. */
	person: FeedPerson;
}

/**
 * Person slot for a company-only feed card whose lead is still a "SELECT" placeholder
 * (PREC-3635): a "No contact yet" header + a "Select Prospect" CTA. Mirrors the list-page
 * NoContactYet flow — the shared {@link GlobalSearchModal} scoped to the card's company, then
 * {@link useSelectProspectForListPersonMutation} to attach the chosen person to the placeholder
 * list_person. On success the feed refetches so the card upgrades to a real identity card; when
 * the row was filled meanwhile (409) a toast says so, the search closes and the feed refetches too.
 */
export const FeedSelectProspect: FunctionComponent<FeedSelectProspectProps> = ({
	card,
	person,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const [showSearchModal, setShowSearchModal] = useState(false);
	const [selectProspect] = useSelectProspectForListPersonMutation();

	const companyName = card.company.name ?? "";
	const companyDomain = card.company.websiteUrl
		? extractDomain(card.company.websiteUrl)
		: undefined;

	const handleConfirm = async ([linkedinUrl]: string[]) => {
		if (!linkedinUrl) {
			return;
		}

		try {
			await selectProspect({
				listPersonId: person.listPersonId,
				linkedinUrl,
				trackerOrganizationId: card.company.trackerOrganizationId,
			}).unwrap();

			dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
		} catch (error) {
			// Someone filled the row meanwhile (409): say so in a toast, close the search and refresh
			// the card to show them (PREC-4570). The refresh turns this slot into a filled card, which
			// unmounts the search modal, so its own snackbar would never be seen (QA comment 27611).
			if (isRowTakenError(error)) {
				showGlobalToast(t("errors.rowNoLongerPlaceholder"));
				setShowSearchModal(false);
				// The lead too, as the mutation does on success: otherwise the panel keeps the
				// placeholder and offers "Select prospect" again for a filled row (QA comment 27628).
				dispatch(api.util.invalidateTags(["Feed", "FeedCounts", "ListPerson"]));

				return;
			}

			throw error;
		}
	};

	return (
		<div
			// Bordered like the person/company identity cards (border-gray-200 rounded-lg p-3) so the
			// empty "No contact yet" slot reads as a card, matching the layout once a prospect exists.
			className="flex min-w-0 flex-1 flex-col items-start gap-1.5 rounded-lg border border-gray-200 p-3"
			data-feed-noselect="true"
			data-testid="feed-select-prospect"
			onClick={(event) => event.stopPropagation()}
		>
			<span className="flex items-center gap-1.5 text-sm font-medium text-text-meta">
				<Icon className="h-3.5 w-3.5" icon={faUserSlash} />
				{t("messages.noContactYet")}
			</span>
			<Button
				color={ButtonColor.BRAND}
				image={<Icon className="mr-2" icon={faUserPlus} />}
				size={ButtonSize.XS}
				title={t("list.selectProspect")}
				onClick={(event) => {
					event.stopPropagation();
					setShowSearchModal(true);
				}}
			/>
			{showSearchModal && (
				<GlobalSearchModal
					singleSelect
					confirmLabel={t("list.selectProspect")}
					initialFilterCompany={
						companyDomain ? { name: companyName, domain: companyDomain } : undefined
					}
					isOpened={showSearchModal}
					setIsOpened={setShowSearchModal}
					onConfirmSelection={handleConfirm}
				/>
			)}
		</div>
	);
};
